/* eslint-disable max-lines */
import { createModel } from '@rematch/core';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import {
  fetchContifyData,
  fetchKlueData,
  fetchSimilarwebData,
  fetchCraftData,
  fetchPdlData,
  fetchSalesforceInstance,
  fetchSalesforceOpportunities,
  fetchPredictLeads,
} from 'api/endpoints/dynamic-data';
import { getFormulaKeyType } from 'klue-html/formulas';
import store from 'store';
import { mergeMapValues } from 'store/utils';

import {
  type FetchProviderDataParamsType,
  type FetchSalesforceDataType,
  type PopulatePayloadType,
  ProvidersEnum,
  type SalesforceInstanceOpportunitiesType,
  type SalesforceInstanceOpportunityValueType,
  type SalesforceMetadataType,
  SALES_FORCE_ERROR,
} from './dynamic-data.model.types';
import { normalizeCompanySFDCData } from './dynamic-data.utils';

import type {
  ContifyReturnType,
  CraftReturnType,
  KlueReturnType,
  SalesforceOpportunitiesReturnType,
  SimilarwebReturnType,
  PredictLeadsReturnType,
  SalesforceOpportunity,
  PdlDataReturnType,
} from 'api/endpoints/dynamic-data.types';
import type { RootModel } from 'store/models';

// In-flight SFDC fetch promises keyed by cache key, so duplicate callers
// can await the same request instead of returning before data is ready.
const sfdcInflightRequests = new Map<string, Promise<void>>();

// In-flight SFDC instance fetch, shared so a board full of SFDC cards
// triggers a single request instead of one per card.
let salesforceInstanceInflight: Promise<void> | null = null;

export const providersFetchMethodMap = {
  [ProvidersEnum.craft]: fetchCraftData,
  [ProvidersEnum.similarweb]: fetchSimilarwebData,
  [ProvidersEnum.klue]: fetchKlueData,
  [ProvidersEnum.contify]: fetchContifyData,
  [ProvidersEnum.predictLeads]: fetchPredictLeads,
  [ProvidersEnum.pdl]: fetchPdlData,
};

const PROVIDERS_TO_SKIP = [ProvidersEnum.Salesforce];

export type StateType = {
  byId: Map<
    string,
    {
      [ProvidersEnum.craft]?: CraftReturnType;
      [ProvidersEnum.similarweb]?: SimilarwebReturnType;
      [ProvidersEnum.klue]?: KlueReturnType;
      [ProvidersEnum.contify]?: ContifyReturnType;
      [ProvidersEnum.Salesforce]?: {
        opportunities?: SalesforceOpportunitiesReturnType;
      };
      [ProvidersEnum.predictLeads]: PredictLeadsReturnType;
      [ProvidersEnum.pdl]?: PdlDataReturnType;
    }
  >;
  loadedKeys: Set<string>;
  SalesforceMetadata: SalesforceMetadataType;
};

export const initialState = {
  byId: new Map(),
  loadedKeys: new Set(),
  SalesforceMetadata: {
    status: undefined,
    instanceOpportunities: {},
    notesTitle: undefined,
    notesField: undefined,
    opportunityValue: {},
    competitorsField: undefined,
  },
} as StateType;

export const dynamicData = createModel<RootModel>()({
  state: initialState,
  reducers: {
    reset: () => initialState,
    populate: (
      state: StateType,
      { provider, data, key }: PopulatePayloadType,
    ) => {
      const currentData = state.byId.get(key)?.[provider] ?? {};

      return {
        ...state,
        byId: mergeMapValues(state.byId, {
          [key]: {
            [provider.toString()]: {
              ...currentData,
              ...data,
            },
          },
        }),
      };
    },
    addLoadingKey: (state: StateType, key: string) => {
      return {
        ...state,
        loadedKeys: state.loadedKeys.add(key),
      };
    },
    removeLoadingKey: (state: StateType, key: string) => {
      const newLoadedKeys = new Set(state.loadedKeys);
      newLoadedKeys.delete(key);

      return {
        ...state,
        loadedKeys: newLoadedKeys,
      };
    },
    setSalesforceError: (state: StateType, error: SALES_FORCE_ERROR) => {
      return {
        ...state,
        SalesforceMetadata: {
          ...state.SalesforceMetadata,
          status: 'error' as const,
          error,
        },
      };
    },
    setSalesforceInstance: (
      state: StateType,
      {
        instance,
        opportunities,
        notesTitle,
        notesField,
        opportunityValue,
        competitorsField,
      }: {
        instance: string;
        opportunities: SalesforceInstanceOpportunitiesType;
        notesTitle?: string;
        notesField?: keyof SalesforceOpportunity;
        opportunityValue?: SalesforceInstanceOpportunityValueType;
        competitorsField?: string[];
      },
    ) => {
      return {
        ...state,
        SalesforceMetadata: {
          ...state.SalesforceMetadata,
          instance,
          status: 'hasInstance' as const,
          instanceOpportunities: opportunities,
          notesTitle,
          notesField,
          opportunityValue,
          competitorsField,
        },
      };
    },
  },
  selectors: (slice, createSelector) => ({
    getInstanceOpportunities() {
      return slice(
        ({ SalesforceMetadata }) => SalesforceMetadata.instanceOpportunities,
      );
    },
    allFetchesCompleted() {
      return slice(({ loadedKeys }) => {
        return loadedKeys.size === 0;
      });
    },
  }),
  effects: (dispatch) => ({
    // Lazily fetch the Salesforce instance (and its card metadata) the first
    // time an SFDC card needs it, instead of eagerly on every company load.
    // Guarded to fetch at most once per resolved outcome; dedupes callers.
    async ensureSalesforceInstance(_payload: void, rootState) {
      const sfdc = rootState.auth.company?.companyData?.sfdc;

      // No SFDC configured for this company — nothing to load.
      if (isEmpty(sfdc)) return;

      const { instance, status, error } =
        rootState.dynamicData.SalesforceMetadata;

      // Already resolved: we have an instance / a prior success / a confirmed
      // "no instance". Transient API errors are allowed to retry.
      if (instance || status === 'hasInstance') return;
      if (status === 'error' && error === SALES_FORCE_ERROR.NO_INSTANCE) return;

      // Dedupe concurrent callers so multiple SFDC cards share one request.
      if (salesforceInstanceInflight) {
        await salesforceInstanceInflight;
        return;
      }

      salesforceInstanceInflight = (async () => {
        try {
          const {
            data: { instance: fetchedInstance },
          } = await fetchSalesforceInstance();

          if (!fetchedInstance) {
            this.setSalesforceError(SALES_FORCE_ERROR.NO_INSTANCE);
            return;
          }

          const {
            opportunityCondition = {},
            notesTitle = '',
            notesField = '',
            opportunityValue = {},
            competitorsField = ['MainCompetitors__c', 'text'],
          } = sfdc[fetchedInstance] ?? {};

          this.setSalesforceInstance({
            instance: fetchedInstance,
            opportunities: normalizeCompanySFDCData(opportunityCondition),
            notesTitle,
            notesField,
            opportunityValue,
            competitorsField,
          });
        } catch {
          this.setSalesforceError(SALES_FORCE_ERROR.API);
        }
      })();

      try {
        await salesforceInstanceInflight;
      } finally {
        salesforceInstanceInflight = null;
      }
    },
    async fetchSalesforceData(
      { query, cardId, extra }: FetchSalesforceDataType,
      rootState,
    ) {
      // Ensure the SF instance metadata is loaded before we rely on it below.
      await this.ensureSalesforceInstance();

      const { dynamicData, rivals } = rootState;
      const { sfdcQueryType = '', profileId } = extra;

      const associetedRivalId = rivals.byProfileId.get(profileId.toString());

      if (!associetedRivalId) {
        return this.setSalesforceError();
      }

      const linkedRival = rivals.byId.get(associetedRivalId);

      if (!linkedRival) {
        return this.setSalesforceError();
      }

      const queryParams = { query: linkedRival.name };

      if (query.key === 'currentRival') {
        query.key = associetedRivalId.toString();
      }

      const key = JSON.stringify({
        key: query.key,
        sfdcQueryType,
      });

      // Read fresh — ensureSalesforceInstance above may have just populated it.
      const { instanceOpportunities } =
        store.getState().dynamicData.SalesforceMetadata;

      if (dynamicData.loadedKeys.has(key)) {
        // If another caller is still fetching, wait for it so we don't
        // resolve before the store is populated. If the fetch already
        // completed, the map entry will be gone and we return immediately.
        const inflight = sfdcInflightRequests.get(key);

        if (inflight) {
          await inflight;
        }

        return false;
      } else {
        this.addLoadingKey(key);
      }

      // When salesforce formula contains additional query paramater
      // we need to identify and match it using sfdc values in company data.
      // then we get the correct query value and send this to the api request
      if (sfdcQueryType && !!instanceOpportunities) {
        const matchKey = instanceOpportunities[sfdcQueryType];

        if (matchKey?.name) {
          Object.assign(queryParams, {
            conditionName: matchKey.name,
          });
        }
      }

      const fetchPromise = fetchSalesforceOpportunities({
        query: queryParams,
      }).then((res) => {
        const { data: opportunities } = res;

        this.populate({
          provider: ProvidersEnum.Salesforce,
          data: opportunities as SalesforceOpportunity[],
          key,
        });
      });

      sfdcInflightRequests.set(key, fetchPromise);
      try {
        await fetchPromise;
      } finally {
        sfdcInflightRequests.delete(key);
      }
    },
    async getQueryParams(
      { cardId, query, extra }: FetchProviderDataParamsType,
      rootState,
    ): Promise<any> {
      const type = getFormulaKeyType(query.key.toString());
      const isDynamicKeyValue = type === 'dynamic';
      const extraParams = omit(query, 'key');

      if (type === 'domain') {
        return { domain: query.key, ...extraParams };
      }

      // currentCompany or currentRival
      if (isDynamicKeyValue) {
        if (query.key === 'currentRival') {
          const currentRivalId = rootState.profiles.currentRivalId;
          const currentProfileId = rootState.profiles.currentProfileId;

          if (!extra.profileId) {
            return null;
          }

          if (extra.profileId === currentProfileId && currentRivalId) {
            return { rivalId: currentRivalId, ...extraParams };
          }

          const cardRelatedRival =
            await dispatch.rivals.loadOrFetchRivalByProfileId(extra.profileId);

          if (cardRelatedRival?.id) {
            return { rivalId: cardRelatedRival.id, ...extraParams };
          }

          return null;
        }

        if (query.key === 'currentCompany') {
          return { rivalId: 0, ...extraParams };
        }
      }

      return { rivalId: query.key, ...extraParams };
    },
    async fetchProviderData(
      { provider, query, cardId, extra }: FetchProviderDataParamsType,
      rootState,
    ) {
      if (PROVIDERS_TO_SKIP.includes(provider)) return;

      // TODO: [Tech-Debt] - TS Bug when using "this", can't detect response type.
      const queryParams: any = await this.getQueryParams({
        query,
        provider,
        cardId,
        extra,
      });

      const fetchMethod = providersFetchMethodMap[provider];

      if (query.key === 'currentRival' && queryParams?.rivalId) {
        query.key = queryParams.rivalId.toString();
      }

      const key = JSON.stringify(query);
      const uniqueTypeKey = `${provider}-${key}`;

      if (rootState.dynamicData.loadedKeys.has(uniqueTypeKey)) {
        return false;
      } else {
        this.addLoadingKey(uniqueTypeKey);
      }

      try {
        const { data } = await fetchMethod({
          query: queryParams,
        } as any);

        this.populate({
          key,
          provider,
          data,
        });

        return data;
      } catch (error) {
        return { error: true };
      } finally {
        await this.removeLoadingKey(uniqueTypeKey);
      }
    },
  }),
});
